import { capitalizeFirstletter, money } from "utils/helpers";
import {
  computeDisplayCart,
  computeVisibleCart,
  sumLineItemsTotal,
} from "utils/cart";
import {
  buildOfferData,
  buildUserFriendlyPaymentSchedule,
} from "containers/MultistepForm/helpers";
import { mergeRight } from "ramda";
import { PROVIDER_DISPLAY_NAME } from "./constants";

export const getPerUnitLabel = (productType) => {
  const type = (productType || "").toLowerCase();
  if (type === "wheel") return "per wheel";
  if (type === "tire") return "per tire";
  return "per item";
};

export const getProductNoun = (productType) => {
  const type = (productType || "").toLowerCase();
  if (type === "tire") return { singular: "tire", plural: "tires" };
  return { singular: "item", plural: "items" };
};

export const getProviderName = (offer) => {
  if (!offer) return "";
  return (
    PROVIDER_DISPLAY_NAME[offer.specific_provider_name] ||
    offer.specific_provider_name ||
    offer.provider ||
    ""
  );
};

export const pickHighestApproval = (offers) => {
  if (!offers || !offers.length) return null;
  return offers.reduce((best, cur) => {
    const a = parseFloat(best.approved_amount) || 0;
    const b = parseFloat(cur.approved_amount) || 0;
    return b > a ? cur : best;
  }, offers[0]);
};

export const dedupeItemName = (item) => {
  const candidates = [item.brand, item.model, item.title]
    .filter((s) => typeof s === "string" && s.trim())
    .map((s) => s.trim());
  const kept = [];
  for (const c of candidates) {
    const subsumedIdx = kept.findIndex((k) => c.includes(k));
    if (subsumedIdx !== -1) {
      kept[subsumedIdx] = c;
      continue;
    }
    if (kept.some((k) => k.includes(c))) continue;
    kept.push(c);
  }
  return kept.join(" ") || "";
};

const buildOptionForOffer = (offer, cartTotal) => {
  const base = (offer.options && offer.options[0]) || {};
  return mergeRight(base, {
    app_id: offer.app_id,
    provider: offer.provider,
    type: offer.type,
    payment_schedule: offer.payment_schedule,
    approved_amount: offer.approved_amount,
    specific_provider_name: offer.specific_provider_name,
    cart_total: cartTotal,
    bank_data_is_required: offer.bank_data_is_required,
  });
};

const findRowValue = (rows, title) => {
  if (!rows) return null;
  const target = title.toLowerCase();
  for (const group of rows) {
    for (const row of group || []) {
      if (
        row &&
        typeof row.title === "string" &&
        row.title.toLowerCase() === target
      ) {
        return row.value;
      }
    }
  }
  return null;
};

export const getPlanDetailRows = (offer, cartTotal = 0) => {
  const option = buildOptionForOffer(offer, cartTotal);
  const data = buildOfferData(option, {}) || [];

  const typeOfPlanRaw = findRowValue(data, "Type of Plan");
  const typeOfPlan =
    typeOfPlanRaw && typeOfPlanRaw !== "undefined"
      ? typeOfPlanRaw
      : offer.type || "Lease to own";
  const freqRaw = findRowValue(data, "Payment Frequency");
  const paymentFrequency =
    freqRaw ||
    buildUserFriendlyPaymentSchedule(option.payment_schedule) ||
    capitalizeFirstletter(option.payment_schedule || "") ||
    "Biweekly";
  const required = offer.bank_data_is_required
    ? "Checking and routing numbers"
    : "Credit or Debit Card";
  const creditReporting = findRowValue(data, "Payment Credit Reporting") || "No";

  return [
    { label: "Type of plan", value: typeOfPlan },
    { label: "Payment frequency", value: paymentFrequency },
    { label: "Required to complete", value: required },
    { label: "Payment credit reporting", value: creditReporting },
  ];
};

export const getOfferTerms = (offer, cartTotal = 0) => {
  const rows = getPlanDetailRows(offer, cartTotal);
  const find = (label) => {
    const row = rows.find((r) => r.label === label);
    return (row && row.value) || "";
  };
  return {
    leaseType: find("Type of plan"),
    frequency: find("Payment frequency"),
  };
};

export const getEarlyOwnershipOptions = (offer) => {
  const opt = (offer.options && offer.options[0]) || {};
  const out = [];
  if (opt.hundred_day_option) {
    out.push({ label: "100-Day Option", text: opt.hundred_day_option });
  } else if (opt.early_purchase_option_amount) {
    out.push({
      label: "100-Day Option",
      text: `Pay off your account within 100 days and pay only $${money(
        opt.early_purchase_option_amount
      )}`,
    });
  }
  if (opt.early_buyout) {
    out.push({ label: "Early Buyout Option", text: opt.early_buyout });
  } else if (opt.early_buyout_discount) {
    out.push({
      label: "Early Buyout Option",
      text: `Pay off your account early and save ${opt.early_buyout_discount}%`,
    });
  }
  return out;
};

export const buildRecommendationsKey = (itemId, approvedAmount, remainingDeficit) =>
  `${itemId}:${approvedAmount}:${remainingDeficit}`;

export const findOfferByProvider = (offers, providerName) => {
  if (!providerName) return null;
  return (offers || []).find(
    (o) => o.specific_provider_name === providerName
  ) || null;
};

export const getOffersForMode = (mode, availableApplications, notAvailableApplications) => {
  if (mode === "under") {
    return [
      ...(availableApplications || []),
      ...(notAvailableApplications || []),
    ];
  }
  return notAvailableApplications || [];
};

export const computeRemainingDeficit = (approvedAmount, cartTotal) => {
  const deficit = cartTotal - (parseFloat(approvedAmount) || 0);
  return Math.max(Math.round(deficit * 100) / 100, 0);
};

export const readChangeFlow = ({
  recommendationsByItem,
  recommendationsByItemLoading,
  itemId,
  approvedAmount,
  remainingDeficit,
}) => {
  if (!itemId || !approvedAmount) return null;
  const key = buildRecommendationsKey(itemId, approvedAmount, remainingDeficit);
  const cached = recommendationsByItem && recommendationsByItem[key];
  return {
    recommendations: (cached && cached.recommendations) || [],
    loading: !!(recommendationsByItemLoading && recommendationsByItemLoading[key]),
    quantity: (cached && cached.quantity) || null,
    discountRequired: !!(cached && cached.discount_required),
    error: (cached && cached.error) || null,
  };
};

export const buildScreenViewModel = ({
  mode,
  items,
  cartAmount,
  pendingChanges,
  allOffers,
  selectedLenderProvider,
}) => {
  const baseLineItems = (items && items.line_items) || [];
  const visibleLineItems = computeVisibleCart(baseLineItems, pendingChanges);
  const displayLineItems = computeDisplayCart(baseLineItems, pendingChanges);
  const removedItems = baseLineItems.filter((i) =>
    (pendingChanges.removedItemIds || []).includes(i.id)
  );

  const rawCartTotal = baseLineItems.length
    ? sumLineItemsTotal(visibleLineItems)
    : parseFloat(cartAmount || (items && items.total_price) || 0);
  const newCartTotal = Math.round(rawCartTotal * 100) / 100;

  const maxApproval = allOffers.length
    ? Math.max(...allOffers.map((o) => parseFloat(o.approved_amount) || 0))
    : 0;

  const selectedOffer = findOfferByProvider(allOffers, selectedLenderProvider);
  const selectedApproved = selectedOffer
    ? parseFloat(selectedOffer.approved_amount) || 0
    : 0;
  const selectedLenderName = selectedOffer ? getProviderName(selectedOffer) : null;

  const heroLenderOffer = pickHighestApproval(allOffers);
  const heroLenderName = heroLenderOffer ? getProviderName(heroLenderOffer) : null;

  const underByMax = Math.max(maxApproval - newCartTotal, 0);
  const overByMax = Math.max(newCartTotal - maxApproval, 0);
  const overForSelected = selectedOffer
    ? Math.max(newCartTotal - selectedApproved, 0)
    : 0;
  const fits = !!selectedOffer && selectedApproved >= newCartTotal;

  const headerFits = selectedOffer ? fits : mode === "under";
  const headerLenderName = fits ? selectedLenderName : heroLenderName;
  const headerUnderAmount = fits
    ? Math.max(selectedApproved - newCartTotal, 0)
    : underByMax;

  return {
    visibleLineItems,
    displayLineItems,
    removedItems,
    newCartTotal,
    maxApproval,
    selectedOffer,
    selectedApproved,
    selectedLenderName,
    heroLenderOffer,
    heroLenderName,
    underByMax,
    overByMax,
    overForSelected,
    fits,
    headerFits,
    headerLenderName,
    headerUnderAmount,
  };
};

export const resolveCta = ({
  submitting,
  hasPending,
  fits,
  selectedOffer,
  selectedLenderName,
  overForSelected,
  onSubmitChanges,
  onContinueWithLender,
}) => {
  if (!selectedOffer) {
    return { text: "Choose a Lender to Continue", disabled: true };
  }
  const overHelper = `Remove at least $${money(overForSelected)} to continue with ${selectedLenderName}`;

  if (fits && !hasPending) {
    return {
      text: `Continue with ${selectedLenderName}`,
      disabled: submitting,
      onClick: onContinueWithLender,
      hint: `You'll review ${selectedLenderName}'s payment plan before completing your order.`,
    };
  }

  if (hasPending) {
    return {
      text: "Update Cart to Continue",
      disabled: !fits || submitting,
      onClick: onSubmitChanges,
      helper: !fits ? overHelper : undefined,
    };
  }

  return {
    text: "Update Cart to Continue",
    disabled: true,
    helper: overHelper,
  };
};
